import { useMemo } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import {
  CheckCircle2,
  Mail,
  Clock,
  KeyRound,
  ArrowRight,
  ArrowLeft,
} from 'lucide-react';
import { AuthThemeToggle } from '../../components/AuthThemeToggle';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Logo } from '../../components/Logo';
import { cn } from '../../lib/cn';

/**
 * /become-a-reseller/submitted — confirmation after a reseller-application
 * submission. Reads `?id=...` from the query string when present (the wizard
 * always passes one); falls back to a freshly-generated demo id.
 *
 * Routing for the actual `/become-a-reseller/status` link is being wired by
 * a parallel agent — we route to it speculatively.
 */
export function BecomeAResellerSubmitted() {
  const navigate = useNavigate();
  // The router schema isn't pre-defined for this page, so cast to bypass.
  const search = useSearch({ strict: false }) as { id?: string; email?: string };

  // Generate a stable id once per render mount.
  const applicationId = useMemo(() => {
    if (search.id) return search.id;
    const rand = Math.random().toString(36).slice(2, 8).toLowerCase();
    return `app_new_${rand}`;
  }, [search.id]);

  // Email shown in the subtitle. We don't get it from the wizard's state,
  // so fall back to the query param or a generic phrase.
  const email = search.email ?? 'the email you provided';

  const timeline: Array<{ icon: typeof Clock; title: string; sub: string }> = [
    {
      icon: Clock,
      title: 'Admin reviews your application',
      sub: 'Usually within 1–2 business days.',
    },
    {
      icon: Mail,
      title: 'We email you the decision',
      sub: 'Approved, declined, or "we need more info."',
    },
    {
      icon: KeyRound,
      title: 'On approval — finish setup',
      sub:
        'New users set a password from the email link. Existing customers click through to onboarding directly.',
    },
  ];

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark relative flex items-center justify-center px-4 py-12">
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10">
        <AuthThemeToggle />
      </div>
      <div className="w-full max-w-2xl">
        <div className="mb-8 flex justify-center">
          <Link to="/" className="flex items-center gap-2.5">
            <Logo />
            <span className="font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
              Possignin
            </span>
          </Link>
        </div>

        <div className="flex flex-col items-center mb-8 text-center">
          <div
            className={cn(
              'w-16 h-16 grid place-items-center rounded-full',
              'bg-emerald-100 text-emerald-700',
              'dark:bg-emerald-950/50 dark:text-emerald-400',
            )}
          >
            <CheckCircle2 className="w-9 h-9" strokeWidth={1.75} />
          </div>
          <h1 className="mt-5 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Application received
          </h1>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300 max-w-md">
            We'll respond within 1–2 business days. We've sent a confirmation to {email}.
          </p>
        </div>

        <Card className="p-5 sm:p-7 mb-6">
          <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/40 px-4 py-3 sm:px-5 sm:py-4 mb-6">
            <div className="text-xs uppercase tracking-wider text-zinc-500 dark:text-zinc-400 font-medium">
              Reference number
            </div>
            <div className="mt-1 mono text-base sm:text-lg font-medium text-zinc-950 dark:text-zinc-50 break-all">
              {applicationId}
            </div>
            <p className="mt-1.5 text-xs text-zinc-500 dark:text-zinc-400">
              Save this — quote it if you contact us about your application.
            </p>
          </div>

          <h2 className="text-base font-semibold text-zinc-950 dark:text-zinc-50 mb-4">
            What happens next
          </h2>
          <ol className="space-y-4">
            {timeline.map((item, idx) => {
              const Icon = item.icon;
              return (
                <li key={idx} className="flex items-start gap-3">
                  <span
                    className={cn(
                      'w-8 h-8 grid place-items-center rounded-full text-xs font-semibold tabular-nums flex-shrink-0',
                      'bg-brand-subtle text-brand',
                      'dark:bg-brand-dm-subtle/30 dark:text-brand-dm',
                    )}
                    aria-hidden="true"
                  >
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Icon className="w-4 h-4 text-zinc-500 dark:text-zinc-400" />
                      <span className="text-sm font-medium text-zinc-950 dark:text-zinc-50">
                        {item.title}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-zinc-600 dark:text-zinc-300">
                      {item.sub}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        </Card>

        <div className="flex items-center justify-center gap-3 flex-wrap">
          <Button
            variant="secondary"
            iconLeft={<ArrowLeft className="w-4 h-4" />}
            onClick={() => navigate({ to: '/' })}
          >
            Back to home
          </Button>
          <Button
            iconRight={<ArrowRight className="w-4 h-4" />}
            onClick={() =>
              navigate({
                to: '/become-a-reseller/status' as never,
                search: { id: applicationId } as never,
              })
            }
          >
            Check application status
          </Button>
        </div>

        <p className="mt-8 text-center text-xs text-zinc-500 dark:text-zinc-400">
          Forgot to add something?{' '}
          <a
            href="mailto:partner@itcitysoft.com"
            className="font-medium text-brand dark:text-brand-dm hover:underline"
          >
            partner@itcitysoft.com
          </a>
        </p>

        <div className="mt-10 flex items-center justify-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
          <Logo className="w-5 h-5 rounded-md" />
          <span>Possignin · partner program</span>
        </div>
      </div>
    </div>
  );
}
